import React from 'react';
import { Button } from 'primereact/button';

/**
 * App-wide safety net. Without it, any render error anywhere unmounted the whole
 * tree and left the borrower on a blank white page — the worst dead end there is.
 * This shows a T8 status page with two ways out instead. It never shows the
 * error itself (no stack traces in the UI); only the message goes to the console.
 *
 * `resetKey` (the current pathname) clears the error when the borrower navigates,
 * so one broken page does not keep the whole app on this screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('Screen failed to render:', error?.message || 'Unknown error');
  }

  componentDidUpdate(prev) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex align-items-center justify-content-center min-h-screen surface-ground p-3">
        <div className="surface-card border-1 surface-border border-round p-5 text-center w-full" style={{ maxWidth: '28rem' }} role="alert">
          <i className="pi pi-exclamation-triangle text-orange-500 pi-2x mb-3" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-900 m-0 mb-2">Something went wrong on this screen</h1>
          <p className="text-600 text-sm line-height-3 m-0 mb-4">
            Your data is safe. Reload the page to try again, or go back to your dashboard.
          </p>
          <div className="flex flex-column sm:flex-row justify-content-center gap-2">
            <Button label="Go to dashboard" icon="pi pi-home" severity="secondary" outlined onClick={() => { window.location.href = `${process.env.PUBLIC_URL || ''}/dashboard`; }} />
            <Button label="Reload page" icon="pi pi-refresh" onClick={() => window.location.reload()} />
          </div>
        </div>
      </div>
    );
  }
}
